<template>
  <div class="container-fluid px-0">
    <div
        class="row row-gap-1 mt-4"
        v-for="({ index, ...selector }) in activeSelectors"
        :key="index"
    >
      <h6 class="col-11 -mr-4">
        {{ selector.label }}
        <span v-if="getMinFiles(index)" class="text-danger">
          <b>*</b>
        </span>
      </h6>
      <h6 class="col-1 bg-grey shadow-inner rounded-lg p-1 text-center text-white">
        <vx-tooltip
          classx="text-center"
          :text="getFileCountTooltip(index)"
          position="top"
        >
          {{ `${getFileCount(index)}/${getMaxFiles(index)}` }}
        </vx-tooltip>
      </h6>
      <div
        class="col-6"
        :class="{ 'col-12': !canUploadFromPC(selector.uploadFrom) }"
        v-if="canUploadFromRepo(selector.uploadFrom)"
      >
        <vx-tooltip
          :text="getTooltip(index, 'repo')"
          position="top"
        >
          <b-button
            variant="primary"
            class="w-100"
            v-has-permission="'ED060'"
            v-b-modal="`repository-modal-${index}`"
            :disabled="isDisabled(index)"
          >
            <feather-icon icon="ServerIcon" />
            Seleccionar archivo{{ getMaxFiles(index) > 1 ? 's' : '' }}
          </b-button>
        </vx-tooltip>
      </div>
      <div
        class="col-6"
        :class="{ 'col-12': !canUploadFromRepo(selector.uploadFrom) }"
        v-if="canUploadFromPC(selector.uploadFrom)"
      >
        <vx-tooltip
          :text="getTooltip(index, 'pc')"
          position="top"
        >
          <upload-file
            :disabled="isDisabled(index)"
            color="primary"
            permission="*"
            :accept="selector.accept || defaultFileTypes"
            v-model="experienceFile"
            data-cy="uploadFile"
            button-class="btn-block"
            container-class="w-100"
            upload-to="ed"
            @input="addFile(experienceFile, index, selector)"
            with-sha
          >
            <template v-slot:default>
              <feather-icon icon="UploadIcon" />
              Subir archivo
            </template>
          </upload-file>
        </vx-tooltip>
      </div>
      <div
        class="col-12"
        v-if="canUploadFromRepo(selector.uploadFrom)"
      >
        <repository-selector
          :key="index"
          :modal-id="`repository-modal-${index}`"
          :max-files="selector.maxFiles || 1"
          :required-file-type="selector.type"
          :accepted-file-extensions="selector.accept"
          :add-file="(file) => addFile(file, index, selector)"
          :files="internalFiles[index] || []"
          :remove-file="(file) => removeFile(file, index)"
        />
      </div>
      <div class="col-12 mt-2">
        <file-list
          :files="internalFiles[index] || []"
          @remove-file="(fileId) => removeFile(fileId, index)"
        />
      </div>
      <div class="col-12">
        <v-invalid-feedback v-if="getErrors(index).length">
          {{ getErrors(index) }}
        </v-invalid-feedback>
      </div>
      <div v-if="index < activeSelectors.length - 1" class="col-12">
        <vs-divider class="my-2" />
      </div>
    </div>
  </div>
</template>

<script>
import { evaluateConditions } from '@/febos/global/customDocuments/components/dynamic-file-selector/utils';
import RepositorySelector from '@/febos/global/customDocuments/components/repository-selector/RepositorySelector';
import FileList from '@/febos/global/customDocuments/components/FileList';
import UploadFile from '@/febos/global/_vue/componentes/UploadFile';
import VInvalidFeedback from '@/febos/global/customDocuments/components/VInvalidFeedback';

export default {
  name: 'dynamic-file-selector',
  components: {
    VInvalidFeedback,
    FileList,
    RepositorySelector,
    UploadFile,
  },
  props: {
    objectToEvaluate: {
      type: Object,
      default: null,
    },
    selectors: {
      type: Array,
      required: true,
    },
    files: {
      type: Array,
      default: () => [],
    },
    resetKey: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      experienceFile: null,
      defaultFileTypes: '.pdf, .doc, .docx, .xlsv, .ppt',
      filesErrors: {},
      internalFiles: {},
      removedFileIds: new Set(),
    };
  },
  methods: {
    addFile(file, index, selector) {

      if (!this.internalFiles[index]) this.$set(this.internalFiles, index, []);

      if (file.sha && this.internalFiles[index].some((f) => f.sha === file.sha)) {
        this.$vs.notify({
          title: 'Ups!',
          text: 'Ya has subido este archivo',
          color: 'danger',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          position: 'bottom-right',
          time: 5000,
        });
        return;
      }

      // Asegurar que el archivo tenga el campo origen
      // Prioridad: selector.value > selector.extra.value > selector.label > fallback
      const origen = selector.value
        || selector.extra?.value
        || selector.label
        || `selector-${index}`;

      this.internalFiles[index].push({ ...file, origen });
    },
    removeFile(fileId, index) {
      this.removedFileIds.add(fileId);
      const updatedFiles = this.internalFiles[index].filter((file) => file.id !== fileId);
      this.$set(this.internalFiles, index, updatedFiles);
    },
    isDisabled(index) {
      return this.internalFiles[index]?.length >= this.getMaxFiles(index);
    },
    getTooltip(index, type) {
      const maxFiles = this.getMaxFiles(index);
      if (this.isDisabled(index)) return `Ya has agregado el máximo de archivos permitidos (${maxFiles})`;
      if (type === 'repo') return 'Desde el repositorio';
      return 'Desde tu computador';
    },
    getFileCountTooltip(index) {
      if (this.getFileCount(index) === this.getMaxFiles(index)) return 'Has alcanzado el máximo de archivos permitidos';
      return `Puedes seleccionar ${this.getMaxFiles(index) - this.getFileCount(index)} archivo${this.getMaxFiles(index) > 1 ? 's' : ''} más`;
    },
    getErrors(index) {
      return this.filesErrors[index] || '';
    },
    // Helper method para obtener un selector por índice
    getSelectorByIndex(index) {
      return this.activeSelectors?.find((doc) => doc.index === index);
    },
    getMinFiles(index) {
      const documento = this.getSelectorByIndex(index);
      return documento?.minFiles || 0;
    },
    getMaxFiles(index) {
      const documento = this.getSelectorByIndex(index);
      return documento?.maxFiles || 1;
    },
    getFileCount(index) {
      return this.internalFiles[index]?.length || 0;
    },
    emitFiles() {
      // Obtener archivos de los selectores activos
      const activeFiles = this.activeSelectors.map(
        (selector) => this.internalFiles[selector.index] || [],
      );
      const selectorFiles = activeFiles.reduce((acc, files) => [...acc, ...files], []);

      // Obtener los valores de TODOS los selectores (no solo activos)
      const allSelectorValues = this.selectors.map((selector) => selector.value);

      // Filtrar archivos del prop original que NO están siendo manejados por NINGÚN selector
      const nonSelectorFiles = (this.files || []).filter(
        (file) => file.origen && !allSelectorValues.includes(file.origen),
      );

      // Combinar ambos conjuntos de archivos
      const allFiles = [...selectorFiles, ...nonSelectorFiles];

      this.$emit('update-files', allFiles);
      this.validate();
    },
    validate() {
      this.filesErrors = {};
      this.activeSelectors.forEach((selector) => {
        const minFiles = this.getMinFiles(selector.index);
        const errorMsg = (this.internalFiles[selector.index]?.length || 0) < minFiles
          ? `Debes subir al menos ${minFiles} archivo${minFiles > 1 ? 's' : ''}.`
          : '';

        this.$set(this.filesErrors, selector.index, errorMsg);
      });

      const isValid = !Object.values(this.filesErrors).some((error) => error);
      this.$emit('valid', isValid);
    },
    canUploadFromRepo(uploadFrom) {
      return ['repository', 'both', ''].includes(uploadFrom || '');
    },
    canUploadFromPC(uploadFrom) {
      return ['pc', 'both', ''].includes(uploadFrom || '');
    },
    mapLoadedFiles() {
      // Si no hay selectores, retornar
      if (!this.selectors || this.selectors.length === 0) return;

      // Si no hay archivos en el prop, no hacer nada.
      // Los archivos adjuntados por el usuario directamente en la UI
      // viven solo en internalFiles y no deben borrarse cuando files=[].
      // El reset explícito se maneja a través de la prop resetKey.
      if (!this.files || this.files.length === 0) return;

      // Crear un mapa: origen → archivos
      const filesByOrigen = {};
      this.files.forEach((file) => {
        if (file.origen) {
          if (!filesByOrigen[file.origen]) {
            filesByOrigen[file.origen] = [];
          }
          filesByOrigen[file.origen].push(file);
        }
      });

      // Para cada selector (TODOS, no solo activos), asignar archivos al índice correcto
      this.selectors.forEach((selector) => {
        // Buscar archivos por value, label, o extra.label (para compatibilidad con datos antiguos)
        const possibleOrigins = [
          selector.value,
          selector.label,
          selector.extra?.label,
          selector.extra?.value
        ].filter(Boolean);

        // Usar un Set para evitar duplicados al buscar por múltiples orígenes
        const filesForSelectorSet = new Set();
        possibleOrigins.forEach((origin) => {
          if (filesByOrigen[origin]) {
            filesByOrigen[origin].forEach((file) => {
              filesForSelectorSet.add(file);
            });
          }
        });

        const filesForSelector = Array.from(filesForSelectorSet);

        if (filesForSelector.length > 0) {
          // Filtrar archivos removidos por el usuario y duplicados por id o sha
          const existingFiles = this.internalFiles[selector.index] || [];
          const existingIds = new Set(existingFiles.map((f) => f.id));
          const existingShas = new Set(existingFiles.filter((f) => f.sha).map((f) => f.sha));

          const uniqueFiles = filesForSelector.filter((file) => {
            if (this.removedFileIds.has(file.id)) return false;
            const isDuplicateById = existingIds.has(file.id);
            const isDuplicateBySha = file.sha && existingShas.has(file.sha);
            return !isDuplicateById && !isDuplicateBySha;
          });

          // Asignar archivos al índice usando $set para reactividad
          if (uniqueFiles.length > 0) {
            const mergedFiles = [...existingFiles, ...uniqueFiles];
            this.$set(this.internalFiles, selector.index, mergedFiles);
          }
        }
      });
    },
  },
  computed: {
    activeSelectors() {

      if (!this.objectToEvaluate) {
        return this.selectors;
      }

      const filtered = this.selectors
        .filter((selector) => {
          const result = evaluateConditions(this.objectToEvaluate, selector.conditions);
          return result;
        });
      return filtered;
    },
  },
  watch: {
    activeSelectors: {
      handler() {
        this.mapLoadedFiles();
        this.validate();
      },
      deep: true,
    },
    internalFiles: {
      handler() {
        this.emitFiles();
      },
      deep: true,
    },
    files: {
      handler() {
        this.mapLoadedFiles();
      },
      deep: true,
    },
    resetKey(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.internalFiles = {};
        this.removedFileIds = new Set();
        this.$nextTick(() => {
          this.mapLoadedFiles();
          this.validate();
        });
      }
    },
  },
  mounted() {
    this.mapLoadedFiles();
    this.validate();
  },
};
</script>
